import type { ReferenceDataset } from "@/lib/reference-tables.generated";
import { ENGINE_FACT_MARKER } from "@/lib/seo/engine-fact-marker";
import { taggedHindi } from "@/lib/hindi-runs";

/**
 * Generic engine-verified reference table (ADR-0051 content-depth architecture).
 *
 * Renders a `ReferenceDataset` generated from the canonical astrology data by
 * `execution/gen_reference_tables.py`. Pages NEVER hand-type these rows — they
 * import a dataset constant and drop it in here, so every reference table on the
 * site is accurate + drift-proof by construction (rules 23/24/29).
 *
 * Semantic design tokens only (no arbitrary text sizes / inline styles / vars),
 * horizontally scrollable so wide tables never break the mobile viewport.
 */
export function ReferenceTable({
  dataset,
  heading,
  intro,
  lang = "en",
  minWidthClass = "min-w-[560px]",
}: {
  dataset: ReferenceDataset;
  /** Optional visible heading (recommended for SEO — becomes an <h2>). */
  heading?: string;
  /** Optional lead paragraph above the table. */
  intro?: React.ReactNode;
  lang?: "en" | "hi";
  minWidthClass?: string;
}) {
  const align = lang === "hi" ? "text-right" : "text-left";
  // On an English page a Devanagari run ("Sunday (रविवार)") is marked lang="hi"
  // so an English voice does not spell it out (WCAG 3.1.2, c-a11y-11). A Hindi
  // page is lang="hi" already. The generated rows stay untouched.
  const cellText = (text: string) => (lang === "en" ? taggedHindi(text) : text);
  return (
    <>
      {heading ? (
        <h2 className="mb-4 mt-10 font-serif text-xl font-bold text-ish-dark">{heading}</h2>
      ) : null}
      {intro ? <p className="mb-4 text-body leading-relaxed">{intro}</p> : null}
      <div
        className="mb-6 overflow-x-auto rounded-lg border border-ish-border bg-ish-bg-card"
        {...{ [ENGINE_FACT_MARKER]: dataset.id }}
      >
        <table className={`${minWidthClass} w-full border-collapse ${align} text-caption`}>
          <caption className="sr-only">{dataset.caption}</caption>
          <thead className="bg-ish-accent-light text-ish-dark">
            <tr>
              {dataset.columns.map((col) => (
                <th key={col} className="px-3 py-2 font-semibold">
                  {cellText(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataset.rows.map((row, ri) => (
              <tr key={`${dataset.id}-${ri}`} className="border-t border-ish-border">
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={
                      ci === 0
                        ? "px-3 py-2 font-semibold text-ish-dark align-top"
                        : "px-3 py-2 align-top"
                    }
                  >
                    {cellText(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
